// The reproducibility version triple — RFC-0008 §10.
//
// A run's bits depend not only on (kernel, seed, N) but on the exact compute substrate:
// the SDK semantics, the math-shim implementation, and (at M3) the pinned fdlibm bytes.
// "SDK version pins a math-shim version pins fdlibm bytes — the version triple is part of
// the verification contract." A permalink records the triple under which it was produced so
// a re-run uses the same substrate; a mismatch is surfaced rather than silently re-derived.

export interface VersionTriple {
  /** Engine semantics: chunking, PRNG, reduction order. Bump on any change to those. */
  sdk: string;
  /** Math shim identity: "native" (pre-M3, engine-specific transcendentals) or "fdlibm". */
  shim: string;
  /** Pinned fdlibm byte hash once the WASM shim is active (M3); "n/a" while shim=native. */
  fdlibm: string;
}

// SDK semantics version. Bump if chunking, PRNG, or reduction order ever changes — any of
// those would change result bits and must invalidate old permalinks rather than reproduce
// them wrongly.
export const SDK_VERSION: string = "1";

// M3 active: the shim is fdlibm-via-WASM (src/sdk/math-wasm/), bitwise-identical across
// engines. The fdlibm hash is the sha256 of the pinned wasm bytes — the third leg of the
// reproducibility triple. Bump/regenerate via src/sdk/math-wasm/build.sh; the hash is
// asserted against the bytes at load time (math-shim.ts) and in fdlibm-shim.test.ts.
export const SHIM_VERSION: string = "fdlibm";
export const FDLIBM_HASH: string = "245f189731cd6db0c8165fb7d340c34adb585bef66c8dde7d4b83621b89ff746";

export function currentTriple(): VersionTriple {
  return { sdk: SDK_VERSION, shim: SHIM_VERSION, fdlibm: FDLIBM_HASH };
}

/** Compact "sdk.shim.fdlibm" string for the permalink. */
export function encodeTriple(t: VersionTriple): string {
  return `${t.sdk}.${t.shim}.${t.fdlibm}`;
}

export function decodeTriple(s: string): VersionTriple | null {
  const parts = s.split(".");
  if (parts.length < 3) return null;
  // fdlibm hash may itself contain dots in theory; rejoin the tail.
  const [sdk, shim, ...rest] = parts;
  return { sdk: sdk!, shim: shim!, fdlibm: rest.join(".") };
}
